import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { PatientService } from 'src/services/patient.service'
import { getErrorMessage } from 'src/utils/http.util'
import { useNotification } from 'src/providers/notification.provider'
import { useSession } from 'src/providers/auth.provider'
import {
  CurecallDropdownButton,
  CurecallDropdownItemStyle,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { TPatient } from 'src/types/patient'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useModals } from 'src/providers/modals.provider'
import { PatientAuditModal } from './patient-audit.modal'
import DocumentModal from './document-modal/document-modal.component'
import { PatientInteropLogsModal } from './interop-logs/patient-interop-logs-modal.component'
import { ResourceChangeHistoryModal } from '../resource-change-history/resource-change-history-modal.component'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  EPermissionSpecialAuthorization,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'

export interface Props {
  patient: TPatient
}

const ControlsDropdown = ({ patient }: Props) => {
  const { project, hasPermission, hasSpecialAuthorization } = useCurrentProject()
  const { currentUser } = useSession()
  const { t } = useTranslation()
  const { notify } = useNotification()
  const { openArchiveModal, openDeleteTestPatientModal } = useModals()

  const [isDocumentModalOpened, setIsDocumentModalOpened] = useState<boolean>(false)
  const [isAuditLogsModalOpened, setIsAuditLogsModalOpened] = useState<boolean>(false)
  const [isChangeHistoryModalOpened, setIsChangeHistoryModalOpened] = useState<boolean>(false)
  const [isInteropLogsModalOpened, setIsInteropLogsModalOpened] = useState<boolean>(false)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'i') {
        if (currentUser?.isAdmin) {
          event.preventDefault()
          setIsInteropLogsModalOpened(true)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentUser?.isAdmin])

  const copyDebugData = async () => {
    try {
      const debugData = await PatientService.getPatientDebugData(project.id, patient.id)
      await navigator.clipboard.writeText(JSON.stringify(debugData, null, 2))
      notify({
        title: t('pages.patient-page.copy-debug-data'),
        message: t('pages.patient-page.copy-debug-data-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch (error) {
      notify({
        title: t('pages.patient-page.copy-debug-data-error'),
        message: getErrorMessage(error),
        appearance: ECurecallNotificationAppearance.WARNING,
      })
    }
  }

  const dropdownItems: React.ReactNode[] = []

  const regularItem = (key: string, label: string, onClick: () => void) => (
    <button key={key} type="button" className={CurecallDropdownItemStyle} onClick={onClick}>
      {label}
    </button>
  )

  const adminItem = (key: string, label: string, onClick: () => void) => (
    <button key={key} type="button" className={CurecallDropdownItemStyle} onClick={onClick}>
      <span className="c-flex c-items-center c-gap-2">
        <AdminOnlyBadge />
        {label}
      </span>
    </button>
  )

  if (
    project.electronicSignatureEnabled &&
    hasSpecialAuthorization(EPermissionSpecialAuthorization.CAN_VIEW_PATIENT_IDENTITIES)
  ) {
    dropdownItems.push(
      regularItem('document-signature', t('pages.patient-page.document-signature'), () =>
        setIsDocumentModalOpened(true),
      ),
    )
  }

  if (currentUser?.isAdmin) {
    dropdownItems.push(
      adminItem('patient-audit', t('pages.patient-page.patient-audit'), () =>
        setIsAuditLogsModalOpened(true),
      ),
      adminItem('patient-change-history', t('pages.patient-page.patient-change-history'), () =>
        setIsChangeHistoryModalOpened(true),
      ),
      adminItem('patient-interop-logs', t('pages.patient-page.patient-interop-logs') + ' (ctrl+i)', () =>
        setIsInteropLogsModalOpened(true),
      ),
      adminItem('copy-debug-data', t('pages.patient-page.copy-debug-data'), copyDebugData),
    )
  }

  if (patient.isTestAccount && currentUser?.isAdminOrSales) {
    dropdownItems.push(
      adminItem('delete-test-patient', t('pages.patient-page.delete-test-patient'), () =>
        openDeleteTestPatientModal({ patient }),
      ),
    )
  }

  if (
    !patient.isTestAccount &&
    (hasPermission(EPermissionGrantSubjects.PATIENT, EPermissionGrantActions.UPDATE) || currentUser?.isAdmin)
  ) {
    dropdownItems.push(
      regularItem('archive-patient', t('pages.patient-page.archive-this-account'), () =>
        openArchiveModal({ patient }),
      ),
    )
  }

  if (!dropdownItems.length) return null

  return (
    <>
      <CurecallDropdownButton
        dropdownButtonLabel={t('pages.patient-page.actions')}
        size={ECurecallSize.SMALL}
        customItems={dropdownItems}
        dropDownButtonAppearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
      />

      {isDocumentModalOpened && (
        <DocumentModal
          patient={patient}
          isOpen={isDocumentModalOpened}
          onToggle={() => setIsDocumentModalOpened(false)}
        />
      )}
      {isAuditLogsModalOpened && (
        <PatientAuditModal
          patient={patient}
          isOpen={isAuditLogsModalOpened}
          onToggle={() => setIsAuditLogsModalOpened(false)}
        />
      )}
      {isChangeHistoryModalOpened && (
        <ResourceChangeHistoryModal
          projectId={project.id}
          patientId={patient.id}
          isOpen={isChangeHistoryModalOpened}
          onToggle={() => setIsChangeHistoryModalOpened(false)}
        />
      )}
      {isInteropLogsModalOpened && (
        <PatientInteropLogsModal
          patient={patient}
          isOpen={isInteropLogsModalOpened}
          onToggle={() => setIsInteropLogsModalOpened(false)}
        />
      )}
    </>
  )
}

export default ControlsDropdown
